import { Injectable } from '@angular/core';
import { isBefore } from 'date-fns'
import { NavigateService, AuthService, UtilsService, LanguageService, SigntrustService, PermissionsService, ModulesService } from '@services/index.service';
import { BehaviorSubject, Observable, defer, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import {
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  ErrorCode,
  Response,
  LoginByCertificateRequest,
  LocalStorageKeys,
  AngularPaths,
  UserAuth,
  LoginByTokenRequest,
  UserSession,
  Module,
  Permission,
} from '@models/index.model';
import { Router } from '@angular/router';

@Injectable()
export class SessionService {
  private loggedIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Observable del refresco en curso. Mientras exista, TODAS las peticiones que
   * necesiten refrescar comparten esta misma llamada. Es el núcleo del fix:
   * nunca puede haber dos refresh en paralelo, así que el backend no rota los
   * tokens dos veces invalidando el token recién emitido.
   */
  private _refreshInFlight$: Observable<UserAuth> | null = null;

  /** Ventana de frescura para coalescer la "estampida" de refrescos (ms). */
  private readonly _refreshCoalesceSkewMs = 30_000;

  constructor(
    private _navigate: NavigateService,
    private router: Router,
    private _auth: AuthService,
    private _utils: UtilsService,
    private _lang: LanguageService,
    private _signtrustServ: SigntrustService,
    private _permiss: PermissionsService,
    private _modules: ModulesService
  ) { }

  public initLocalUserSession(loginForm: LoginRequest): Observable<any> {
    return this.buildUserSession(this._auth.logIn(loginForm)).pipe(
      tap(_ => {
        this.routerRedirectBeforeLogin();
      }),
      catchError(err => {
        if (err.status === 405 && err.error.errorCode === ErrorCode.CHANGE_PASSWORD) {
          this.cleanPersonalLocalStorage();
          this._setLoggedIn(false);
          this._navigate.setUrl(`/${AngularPaths.PASSWORD_UPDATE}`, { username: loginForm.username, update: true });
        } else {
          this.removeBrowserSession();
        }
        return throwError(() => err);
      })
    );
  }

  public initCertificateSession(loginByCertificateRequest: LoginByCertificateRequest): Observable<any> {
    return this.buildUserSession(this._auth.loginByCertificate(loginByCertificateRequest)).pipe(
      tap(_ => {
        this.routerRedirectBeforeLogin();
      }),
      catchError(err => {
        this.removeBrowserSession();
        return throwError(() => err);
      })
    );
  }

  public initTokenSession(loginByTokenRequest: LoginByTokenRequest): Observable<any> {
    return this.buildUserSession(this._auth.loginByToken(loginByTokenRequest)).pipe(
      tap(_ => {
        this.routerRedirectBeforeLogin();
      }),
      catchError(err => {
        this.removeBrowserSession();
        return throwError(() => err);
      })
    );
  }

  public refreshSession(refreshTokenRequest: RefreshTokenRequest): Observable<UserAuth> {
    return this.buildUserSession(this._auth.refreshToken(refreshTokenRequest)).pipe(
      map((result: UserSession) => result.userAuth)
    );
  }

  /**
   * Refresco compartido (single-flight) con coalescencia por frescura.
   * Úsalo SIEMPRE en lugar de refreshSession() directamente.
   *
   *  - Si ya hay un refresh en curso, se comparte (nunca dos en paralelo).
   *  - Si el token vigente ya está fresco (otro flujo acaba de refrescar) y NO se
   *    fuerza, se devuelve la sesión actual SIN llamar a la red. Esto cierra la
   *    ventana post-completion en la que una "estampida" de peticiones (arranque
   *    en frío desde un link) encadenaba refrescos que se invalidaban entre sí.
   *  - force=true: fuerza el refresh saltándose la comprobación de frescura. Solo
   *    lo usa el manejador de 401 reactivo, donde el token vigente ha sido
   *    rechazado por el servidor aunque aún no esté caducado por tiempo.
   */
  public refreshSessionShared(options?: { force?: boolean; skewMs?: number; source?: string }): Observable<UserAuth> {
    const source = options?.source ?? 'unknown';

    if (this._refreshInFlight$) {
      // eslint-disable-next-line no-console
      // console.debug(`[refresh-debug] COALESCED (ya había un refresh en curso) source=${source}`);
      return this._refreshInFlight$;
    }

    const skewMs = options?.skewMs ?? this._refreshCoalesceSkewMs;
    if (!options?.force && !this.isAccessTokenExpiring(skewMs)) {
      const current: UserAuth = this.getUserAuth();
      if (current?.accessToken) {
        // eslint-disable-next-line no-console
        // console.debug(`[refresh-debug] SKIPPED (token todavía fresco) source=${source}`);
        return of(current);
      }
    }

    // eslint-disable-next-line no-console
    // console.debug(`[refresh-debug] NUEVO refresh iniciado source=${source} force=${!!options?.force}`);

    // defer: el refreshToken se lee EN EL MOMENTO DE LA SUSCRIPCIÓN, no al crear
    // el observable. Si se leyera al crearlo, una suscripción retrasada podría
    // enviar un refreshToken ya rotado por otro refresco -> 401.
    const refresh$: Observable<UserAuth> = defer(() => {
      const tokenUsed = this.getRefreshToken();
      // eslint-disable-next-line no-console
      // console.debug(`[refresh-debug] ENVIANDO PUT refresh-token source=${source} refreshTokenTail=...${tokenUsed?.slice(-12)}`);
      return this.refreshSession({ refreshToken: tokenUsed }).pipe(
        tap((res: UserAuth) => {
          // eslint-disable-next-line no-console
          // console.debug(`[refresh-debug] EXITO source=${source} nuevoRefreshTokenTail=...${res?.refreshToken?.slice(-12)}`);
        })
      );
    }).pipe(
      // Si el refresh es rechazado (refreshToken inválido/caducado), la sesión
      // está muerta: la limpiamos AQUÍ, en el único punto por el que pasan todos
      // los refrescos, para que TODAS las comprobaciones por presencia
      // (checkTokenValid/hasRefreshToken) sean coherentes de inmediato y no
      // queden tokens "fantasma" que provoquen rebotes de rutas. No navegamos:
      // la decisión de a dónde ir (login + hash) la toman los guards / el flujo.
      catchError((err) => {
        const currentTokenNow = this.getRefreshToken();
        // eslint-disable-next-line no-console
        // console.debug(`[refresh-debug] FALLO source=${source} status=${err?.status} refreshTokenVigenteAhoraTail=...${currentTokenNow?.slice(-12)}`);
        if (this._isRefreshUnauthorized(err)) {
          this.clearBrowserSessionSilently();
        }
        return throwError(() => err);
      }),
      // IMPORTANTE: finalize va ANTES de shareReplay, así se ejecuta cuando la
      // FUENTE termina (éxito o error), no cuando un consumidor se cancela. Si
      // fuese después, la cancelación de un guard o de una petición (teardown de
      // componente, redirect del router) liberaría el carril con la llamada aún
      // en vuelo y el siguiente refresco saldría con el refreshToken antiguo.
      finalize(() => {
        // eslint-disable-next-line no-console
        // console.debug(`[refresh-debug] FINALIZE (carril liberado) source=${source}`);
        this._refreshInFlight$ = null;
      })
    );

    // refCount:false => aunque los consumidores se cancelen, la fuente sigue
    // suscrita y el refresco termina, persistiendo el nuevo token.
    this._refreshInFlight$ = refresh$.pipe(shareReplay({ bufferSize: 1, refCount: false }));

    // Suscripción interna: garantiza que el refresco se ejecute y se complete
    // aunque TODOS los consumidores se cancelen (y que el carril se libere
    // siempre, evitando quedarse bloqueado si nadie llega a suscribirse).
    this._refreshInFlight$.subscribe({ next: () => {}, error: () => {} });

    return this._refreshInFlight$;
  }

  /** Fecha de expiración del accessToken de la sesión de app, o null si no hay sesión. */
  public getExpiration(): Date | null {
    const session: UserAuth = this.getUserAuth();
    return session?.expiration ? new Date(session.expiration) : null;
  }

  /** true si el accessToken ya caducó o caducará dentro de `skewMs` milisegundos. */
  public isAccessTokenExpiring(skewMs = 30_000): boolean {
    const exp = this.getExpiration();
    if (!exp) return false; // sin sesión: no hay nada que refrescar por esta vía
    return Date.now() + skewMs >= exp.getTime();
  }

  /** true si tenemos refreshToken disponible para intentar renovar. */
  public hasRefreshToken(): boolean {
    return !!this.getRefreshToken();
  }

  public getUserAuth(): UserAuth {
    return (this._utils.getLocalStorage(LocalStorageKeys.SESSION_KEY) as UserSession)?.userAuth;
  }

  public routerRedirectBeforeLogin() {
  const spRedirectId = localStorage.getItem('spRedirectId');
  const agentRedirectId = localStorage.getItem('agentRedirectId');
  if (spRedirectId) {
    localStorage.removeItem('spRedirectId'); // evita loops
    this.router.navigate([`/${AngularPaths.SIGNTRUST}/${spRedirectId}`], { replaceUrl: true });
    return;
  }
  if (agentRedirectId) {
    localStorage.removeItem('agentRedirectId'); // evita loops
    this.router.navigate([`/${AngularPaths.SIGN_BY_AGENT}/${agentRedirectId}`], { replaceUrl: true });
    return;
  }
  this._navigate.setUrl(AngularPaths.DEFAULT);
}

  public removeSession(): Observable<any> {
    return this._auth.logout().pipe(
      switchMap((res) => {
        this.removeBrowserSession();
        return of(res);
      }),
      catchError((err) => {
        this.removeBrowserSession();
        return throwError(() => err);
      })
    );
  }

  public removeBrowserSession() {
    this.cleanPersonalLocalStorage();
    this._setLoggedIn(false);
    // Se llama redirect una vez finalizado el logout.
    this._lang.configureApplicationLanguage();
    this._navigate.setUrl(`/${AngularPaths.LOGIN}`);
  }

  /**
   * Limpia la sesión del navegador SIN navegar. La usa refreshSessionShared
   * cuando el refresh es rechazado: deja el estado coherente (sin tokens
   * fantasma) y delega la navegación en quien corresponda (guard / flujo).
   */
  public clearBrowserSessionSilently(): void {
    this.cleanPersonalLocalStorage();
    this._setLoggedIn(false);
  }

  private _isRefreshUnauthorized(err: any): boolean {
    // Cualquier 401/403 sobre el endpoint de refresh implica refreshToken no válido.
    return err?.status === 401 || err?.status === 403;
  }

  public getAccessToken(): string {
    const session: UserAuth = this.getUserAuth();
    return session?.accessToken;
  }

  public getRefreshToken(): string {
    const session: UserAuth = this.getUserAuth();
    return session?.refreshToken;
  }

  public isLoggedIn(): boolean {
    const session: UserAuth = this.getUserAuth();
    let isLogged: boolean = false;
    if (session) {
      isLogged = isBefore(new Date(), new Date(session.expiration));
    }
    return isLogged;
  }

  public loggedIn$(): Observable<boolean> {
    return this.loggedIn.asObservable();
  }

  private _setLoggedIn(loggedIn: boolean) {
    this.loggedIn.next(loggedIn);
  }

  public buildUserSession(authRequest: Observable<Response<LoginResponse>>): Observable<UserSession> {
    return authRequest.pipe(
      map((res: Response<LoginResponse>) => this._utils.mapResponseData<LoginResponse>(res)),
      map((loginResponse: LoginResponse) => ({
        userAuth: {
          accessToken: loginResponse.accessToken,
          refreshToken: loginResponse.refreshToken,
          expiration: loginResponse.expiration,
        },
        userData: {
          friendlyName: loginResponse.sessionConfig.friendlyName,
          email: loginResponse.sessionConfig.email,
          language: this._lang.normalizeLang(loginResponse.sessionConfig.language),
          domainName: loginResponse.sessionConfig.domainName,
          domainType: loginResponse.sessionConfig.domainType,
        },
        userPermissions: loginResponse.sessionConfig.permissions,
        userModules: loginResponse.sessionConfig.modules,
      })),
      tap((res: UserSession) => {
        this._utils.setLocalStorage(LocalStorageKeys.SESSION_KEY, res);
        this._lang.configureApplicationLanguage();
        this._setLoggedIn(true);
      })
    );
  }

  public checkTokenValid(): boolean {
    const session: UserAuth = this.getUserAuth();
    return !!session && !!session.accessToken;
  }

  private cleanPersonalLocalStorage() {
    localStorage.removeItem(LocalStorageKeys.SESSION_KEY);
  }
}
